import React, { Suspense } from 'react';
import { CircularProgress } from '@mui/material';

//#region Imports
// Keep these as regular imports - they're small/common or needed immediately
import NotFoundPage from '../views/NotFoundPage';
import Layout from '../pages/layout';
import LoginPage from '../views/auth/login';
import withAuth from '../hoc/withAuth';
import { useAuth } from '../contexts/AuthContext';
import { Organization } from '../types/organization';
import { lazyWithRetry } from '../utilities/lazyWithRetry';

// Lazy load all view components for code splitting
const Dashboard = lazyWithRetry(() => import('../views/dashboard'));
const OrgPage = lazyWithRetry(() => import('../views/org/OrganizationProfile'));
const UserPage = lazyWithRetry(() => import('../views/user'));
const ImportSupporters = lazyWithRetry(() => import('../views/supporters/import'));
const SupportersView = lazyWithRetry(
  () => import('../views/supporters/view_supporters/SupportersView')
);
const EmailSettings = lazyWithRetry(
  () => import('../views/org_email_settings/EmailSettings')
);
const DigitalBodyLanguage = lazyWithRetry(
  () => import('../views/digital_body_language/DigitalBodyLangauge')
);
const ContentAudit = lazyWithRetry(() => import('../views/content_audit/ContentAudit'));
const EmailCalendar = lazyWithRetry(() => import('../views/emails/EmailCalendar'));
const SelectedEffort = lazyWithRetry(() => import('../views/efforts/SelectedEffort'));
const AdminCRMPage = lazyWithRetry(
  () => import('../views/crm_connections/AdminCRMPage')
);
const AllEfforts = lazyWithRetry(() => import('../views/efforts/AllEfforts'));
const Deliverable = lazyWithRetry(() => import('./efforts/effort_details/Deliverable'));
const SurveyHTML = lazyWithRetry(() => import("./efforts/effort_details/SurveyHTMLView"))
const LegacyChallengeWidget = lazyWithRetry(() => import('./efforts/effort_details/LegacyChallengeWidgetView'));
const TellYourStoryWidget = lazyWithRetry(() => import('./efforts/effort_details/TellYourStoryWidgetView'));
const DafWidget = lazyWithRetry(() => import('./efforts/effort_details/DafWidgetView'));
const DafCustodianWidget = lazyWithRetry(() => import('./efforts/effort_details/DafCustodianWidgetView'));
const MigrateExistingOrganization = lazyWithRetry(
  () => import('../views/migrate_organization')
);
const BackgroundJobsPage = lazyWithRetry(
  () => import('../views/celery_tasks')
);
const SupportersMergePage = lazyWithRetry(
  () => import('../views/supporters_merge')
);
const AssetsManager = lazyWithRetry(
  () => import('../views/assets_manager')
);
const FormEditor = lazyWithRetry(() => import('../views/forms/FormEditor'));

// Module-level auth wrappers: must not be created inside RoutesTree, or any AuthContext
// update (e.g. profile save) re-renders RoutesTree, produces new component types, and
// remounts lazy pages — resetting local state like UserProfile tabs.
const ProtectedDashboard = withAuth(Dashboard);
const ProtectedOrgPage = withAuth(OrgPage);
const ProtectedUserPage = withAuth(UserPage);
const ProtectedImportSupporters = withAuth(ImportSupporters);
const ProtectedSupportersView = withAuth(SupportersView);
const ProtectedEmailSettings = withAuth(EmailSettings);
const ProtectedContentAudit = withAuth(ContentAudit);
const ProtectedEmailOverview = withAuth(EmailCalendar);
const ProtectedSelectedEffort = withAuth(SelectedEffort);
const ProtectedAdminCRMPage = withAuth(AdminCRMPage);
const ProtectedAllEfforts = withAuth(AllEfforts);
const ProtectedDBL = withAuth(DigitalBodyLanguage);
const ProtectedDeliverable = withAuth(Deliverable);
const ProtectedSurveyHTML = withAuth(SurveyHTML);
const ProtectedLegacyChallengeWidget = withAuth(LegacyChallengeWidget);
const ProtectedTellYourStoryWidget = withAuth(TellYourStoryWidget);
const ProtectedDafWidget = withAuth(DafWidget);
const ProtectedDafCustodianWidget = withAuth(DafCustodianWidget);
const ProtectedMigrateExistingOrganization = withAuth(MigrateExistingOrganization);
const ProtectedBackgroundJobsPage = withAuth(BackgroundJobsPage);
const ProtectedSupportersMergePage = withAuth(SupportersMergePage);
const ProtectedAssetsManager = withAuth(AssetsManager);
const ProtectedFormEditor = withAuth(FormEditor);

// #endregion

// Loading fallback component
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
  </div>
);
interface Props {
  isLoggedIn: boolean;
  selectedOrg: Organization | null;
  setSelectedOrg: React.Dispatch<React.SetStateAction<Organization | null>>;
  anchorEl: HTMLElement | null;
  avatarEl: HTMLElement | null;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  setAvatarEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  handleClose: () => void;
  handleOrgSelect: (org: Organization) => void;
}

const RoutesTree: React.FC<Props> = ({
  isLoggedIn,
  selectedOrg,
  setSelectedOrg,
  anchorEl,
  avatarEl,
  setAnchorEl,
  setAvatarEl,
  handleClose,
  handleOrgSelect,
}) => {
  const { activePage } = useAuth();

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedDashboard />
            </Suspense>
          </Layout>
        );
      case 'import-supporters':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
                <ProtectedImportSupporters />
            </Suspense>
          </Layout>
        );
      case 'users':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedUserPage />
            </Suspense>
          </Layout>
        );
      case 'organization':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedOrgPage />
            </Suspense>
          </Layout>
        );
      case 'view-supporters':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
                <ProtectedSupportersView shouldShowImportAction />
            </Suspense>
          </Layout>
        );
      case 'org-email-settings':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedEmailSettings org_id={selectedOrg?.id ?? ''} is_orgProfile={true} />
            </Suspense>
          </Layout>
        );
      case 'content-audit':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedContentAudit />
            </Suspense>
          </Layout>
        );
      case 'email-overview':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedEmailOverview />
            </Suspense>
          </Layout>
        );
      case 'digital-body-language':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedDBL />
            </Suspense>
          </Layout>
        );
      case 'effort-overview':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
                <ProtectedAllEfforts />
            </Suspense>
          </Layout>
        );
      case 'effort-details':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
                <ProtectedSelectedEffort />
            </Suspense>
          </Layout>
        );
      case 'survey-html': 
      return (
        <Layout
          {...{
            selectedOrg,
            setSelectedOrg,
            anchorEl,
            avatarEl,
            setAnchorEl,
            setAvatarEl,
            handleClose,
            handleOrgSelect,
          }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedSurveyHTML />
          </Suspense>
        </Layout>
      );
      case 'tell-your-story-widget':
      case 'legacy-challenge-widget':
      case 'daf-widget':
      case 'daf-custodian-widget': {
        const WidgetView = {
          'tell-your-story-widget': ProtectedTellYourStoryWidget,
          'legacy-challenge-widget': ProtectedLegacyChallengeWidget,
          'daf-widget': ProtectedDafWidget,
          'daf-custodian-widget': ProtectedDafCustodianWidget,
        }[activePage];
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <WidgetView />
            </Suspense>
          </Layout>
        );
      }
      case 'deliverable':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedDeliverable />
            </Suspense>
          </Layout>
        );

      case 'crm-admin':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedAdminCRMPage />
            </Suspense>
          </Layout>
        );
      case 'migrate-organization':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedMigrateExistingOrganization />
            </Suspense>
          </Layout>
        );
      case 'celery-tasks':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedBackgroundJobsPage />
            </Suspense>
          </Layout>
        );
      case 'supporters-merge':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedSupportersMergePage />
            </Suspense>
          </Layout>
        );
      case 'assets-manager':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedAssetsManager />
            </Suspense>
          </Layout>
        );
      case 'form-editor':
        return (
          <Layout
            {...{
              selectedOrg,
              setSelectedOrg,
              anchorEl,
              avatarEl,
              setAnchorEl,
              setAvatarEl,
              handleClose,
              handleOrgSelect,
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              <ProtectedFormEditor />
            </Suspense>
          </Layout>
        );
      case 'login':
        return <LoginPage />;
      default:
        return <NotFoundPage />;
    }
  };

  return <div>{!isLoggedIn ? <LoginPage /> : renderPage()}</div>;
};

export default RoutesTree;
